import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { useCart } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/header.scss";

const categoryLink = (gender, category) =>
  `/shop?gender=${encodeURIComponent(gender)}&category=${encodeURIComponent(
    category
  )}`;

function Header({ home = false }) {
  const { cartCount, wishlistItems } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState("");
  const [query, setQuery] = useState("");
  const menuButtonRef = useRef(null);
  const mobileNavigationRef = useRef(null);
  const searchButtonRef = useRef(null);
  const searchInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setMobileSection("");
  }, [location.pathname, location.search]);

  useEffect(() => {
    const navigationOpen = mobileOpen || searchOpen;
    document.body.classList.toggle("navigation-open", navigationOpen);

    if (searchOpen) {
      window.setTimeout(() => searchInputRef.current?.focus(), 60);
    } else if (mobileOpen) {
      window.setTimeout(() => {
        mobileNavigationRef.current
          ?.querySelector("button:not([disabled]), a[href]")
          ?.focus();
      }, 60);
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (searchOpen) {
        setSearchOpen(false);
        searchButtonRef.current?.focus();
      } else if (mobileOpen) {
        setMobileOpen(false);
        setMobileSection("");
        menuButtonRef.current?.focus();
      }
    };

    if (navigationOpen) document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove("navigation-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen, searchOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    setSearchOpen(false);
    navigate(value ? `/shop?search=${encodeURIComponent(value)}` : "/shop");
  };

  const closeMobileNavigation = () => {
    setMobileOpen(false);
    setMobileSection("");
    menuButtonRef.current?.focus();
  };

  return (
    <header className={`site-header${home ? " site-header--home" : ""}`}>
      <div className="site-header__bar">
        <nav className="header-nav header-nav--desktop" aria-label="Primary">
          {["Women", "Men"].map((gender) => (
            <div className="header-nav__item header-nav__item--mega" key={gender}>
              <Link to={`/shop?gender=${gender}`}>{gender}</Link>
              <div className="mega-menu">
                <div className="mega-menu__intro">
                  <p>{gender}</p>
                  <Link to={`/shop?gender=${gender}`}>View all</Link>
                </div>
                <div className="mega-menu__columns">
                  {siteConfig.navigation[gender.toLowerCase()].map((category) => (
                    <Link key={category} to={categoryLink(gender, category)}>
                      {category}
                    </Link>
                  ))}
                </div>
                <Link className="mega-menu__feature" to={`/shop?gender=${gender}`}>
                  <img
                    src={productData.find((product) => product.gender === gender)?.image}
                    alt=""
                  />
                  <span>Autumn 2026</span>
                </Link>
              </div>
            </div>
          ))}
          <div className="header-nav__item">
            <Link to="/shop?search=fragrance">Fragrances</Link>
          </div>
          <div className="header-nav__item">
            <Link to="/shop?search=sale">Sale</Link>
          </div>
          <div className="header-nav__item">
            <Link to="/about">About</Link>
          </div>
          <div className="header-nav__item header-nav__item--more">
            <button type="button" aria-haspopup="true">More</button>
            <div className="header-more-menu">
              <Link to="/privacy">Privacy policy</Link>
              <Link to="/cookie-policy">Cookie policy</Link>
              <Link to="/terms">Terms and conditions</Link>
              <Link to="/faqs">FAQs</Link>
            </div>
          </div>
        </nav>

        <button
          ref={menuButtonRef}
          className="header-icon header-menu-toggle"
          type="button"
          onClick={() => {
            setSearchOpen(false);
            setMobileSection("");
            setMobileOpen((current) => !current);
          }}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
          title={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <FiX /> : <FiMenu />}
          <span className="sr-only">{mobileOpen ? "Close menu" : "Open menu"}</span>
        </button>

        <Link className="site-logo" to="/" aria-label={`${siteConfig.brandName} home`}>
          <span className="site-logo__mark" aria-hidden="true">J</span>
          <span className="site-logo__word">{siteConfig.logo}</span>
        </Link>

        <nav className="header-actions" aria-label="Utility">
          <span className="header-action header-action--locale">
            Hong Kong / English
          </span>
          <Link className="header-action header-action--service" to="/contact">
            Client service
          </Link>
          <button
            ref={searchButtonRef}
            className="header-action header-action--search"
            type="button"
            onClick={() => {
              setMobileOpen(false);
              setMobileSection("");
              setSearchOpen(true);
            }}
            aria-expanded={searchOpen}
            aria-controls="site-search-dialog"
            aria-label="Search"
          >
            <FiSearch aria-hidden="true" />
            <span className="sr-only">Search</span>
          </button>
          <Link
            className="header-action header-action--wishlist"
            to="/shop?view=wishlist"
            aria-label={`Wishlist${wishlistItems.length ? `, ${wishlistItems.length} items` : ""}`}
          >
            <FiHeart aria-hidden="true" />
            {wishlistItems.length > 0 && (
              <span className="header-action__count">{wishlistItems.length}</span>
            )}
          </Link>
          <Link
            className="header-action"
            to="/cartpage"
            aria-label={`Shopping bag, ${cartCount} ${cartCount === 1 ? "item" : "items"}`}
          >
            <FiShoppingBag aria-hidden="true" />
            {cartCount > 0 && <span className="header-action__count">({cartCount})</span>}
          </Link>
        </nav>
      </div>

      {searchOpen && (
        <div
          id="site-search-dialog"
          className="search-panel is-open"
          role="dialog"
          aria-modal="true"
          aria-labelledby="site-search-title"
        >
          <h2 className="sr-only" id="site-search-title">Search</h2>
          <form className="search-panel__form" onSubmit={handleSearch}>
            <FiSearch aria-hidden="true" />
            <label className="sr-only" htmlFor="site-search">
              Search the collection
            </label>
            <input
              ref={searchInputRef}
              id="site-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search (keywords, etc.)"
              autoComplete="off"
            />
            <button className="search-panel__submit" type="submit" aria-label="Submit search">
              <FiChevronRight aria-hidden="true" />
            </button>
            <button
              className="header-icon search-panel__close"
              type="button"
              onClick={() => {
                setSearchOpen(false);
                searchButtonRef.current?.focus();
              }}
              aria-label="Close search"
            >
              <FiX aria-hidden="true" />
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <nav
          ref={mobileNavigationRef}
          id="mobile-navigation"
          className="mobile-navigation is-open"
          aria-label={mobileSection ? `${mobileSection} categories` : "Mobile primary"}
        >
          {mobileSection ? (
            <div className="mobile-navigation__section">
              <button
                className="mobile-navigation__back"
                type="button"
                onClick={() => setMobileSection("")}
              >
                <FiChevronLeft aria-hidden="true" />
                Back to menu
              </button>
              <p>{mobileSection}</p>
              <Link to={`/shop?gender=${mobileSection}`}>
                View all
                <FiChevronRight aria-hidden="true" />
              </Link>
              {siteConfig.navigation[mobileSection.toLowerCase()].map((category) => (
                <Link key={category} to={categoryLink(mobileSection, category)}>
                  {category}
                  <FiChevronRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          ) : (
            <>
              <div className="mobile-navigation__main">
                {["Women", "Men"].map((gender) => (
                  <button type="button" key={gender} onClick={() => setMobileSection(gender)}>
                    {gender}
                    <FiChevronRight aria-hidden="true" />
                  </button>
                ))}
                <Link to="/shop?search=fragrance">
                  Fragrances
                  <FiChevronRight aria-hidden="true" />
                </Link>
                <Link to="/shop?search=sale">
                  Sale
                  <FiChevronRight aria-hidden="true" />
                </Link>
                <Link to="/about">
                  About
                  <FiChevronRight aria-hidden="true" />
                </Link>
                <Link to="/privacy">Privacy policy<FiChevronRight aria-hidden="true" /></Link>
                <Link to="/cookie-policy">Cookie policy<FiChevronRight aria-hidden="true" /></Link>
                <Link to="/terms">Terms and conditions<FiChevronRight aria-hidden="true" /></Link>
                <Link to="/faqs">FAQs<FiChevronRight aria-hidden="true" /></Link>
              </div>
              <div className="mobile-navigation__service">
                <Link to="/shop?view=wishlist">Wishlist</Link>
                <Link to="/contact">Client service</Link>
                <Link to="/shipping">Hong Kong / English</Link>
              </div>
            </>
          )}
        </nav>
      )}
      {(mobileOpen || searchOpen) && (
        <button
          type="button"
          className="header-backdrop"
          onClick={() => {
            if (searchOpen) {
              setSearchOpen(false);
              searchButtonRef.current?.focus();
            } else {
              closeMobileNavigation();
            }
          }}
          aria-label="Close navigation"
        />
      )}
    </header>
  );
}

export default Header;
